import {ChangeDetectorRef, Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../sdk/src/components/ibase-page/ibase-page.component';
import {CpmCProjectApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {firstValueFrom, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {UserCaching} from '../../caching/user.caching';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    NewEditProjectModalComponent,
    NewEditProjectModalResult
} from '../../modals/new-edit-project-modal/new-edit-project-modal.component';
import {IBaseCRUModalResultBase} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCUserApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCServiceRequestApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCProjectApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {SdkBridge} from '../../sdk-bridge';

/**
 * Project General Information Component
 *
 * Displays general information for a project including:
 * - Read-only metadata (ID, name, organization, created date/user)
 * - Statistics overview (service requests, files, object store counts)
 *
 * Provides navigation to related tabs and modal dialogs for viewing and editing project details.
 */
@Component({
    selector: 'app-project-general-info',
    templateUrl: './project-general-info.component.html',
    styles: `
        .info-col {
            display: flex;
            flex-direction: column;
            align-content: space-between;
            flex-wrap: wrap;
            justify-content: space-around;

            & > div {
                height: 2.5rem;
            }
        }
    `,
    imports: [CommonModule, PrimeNgImportsModule]
})
export class ProjectGeneralInfoComponent extends IBasePageComponent implements OnChanges {
    private readonly cdr = inject(ChangeDetectorRef);

    protected createdByUser?: CpmCUserApiModel;

    protected canEditProject = false;

    protected serviceRequestsCount?: number;
    protected filesCount?: number;
    protected objectStoreCount?: number;

    // ===============================================================
    // ===============================================================

    /**
     * The project to display and edit
     */
    @Input() project?: CpmCProjectApiModel;
    /**
     * Observable that triggers statistics refresh when data changes in other tabs
     */
    @Input() refreshTrigger$?: Observable<void>;

    // ===============================================================
    // ===============================================================

    /**
     * Initializes the component
     */
    constructor() {
        super();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        // Subscribe to refresh trigger to reload statistics when data changes in other tabs
        this.refreshTrigger$?.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.loadCounts();
        });

        this.canEditProject = SdkBridge.isAuthorizedForRouteResourceUpdate(
            CpmCProjectApiService.endpointEdit(undefined, this.project?.uuid ?? '')
        );

        await this.asyncCritical(async () => {
            if (this.project?.created_by_uuid) {
                UserCaching.getUserBatched(this.project.created_by_uuid).subscribe((user) => {
                    this.createdByUser = user;
                });
            }
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // No cleanup required
    }

    // ===============================================================
    // ===============================================================

    /**
     * Angular lifecycle hook called when input properties change.
     * Loads related data when project changes.
     *
     * @param changes - Object containing the changed properties
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['project'] && this.project) {
            this.loadCounts();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Loads count statistics for service requests, files, and object store entries.
     * Updates the component properties with the retrieved counts.
     */
    private loadCounts(): void {
        if (!this.project?.uuid) return;

        // Service Requests count
        firstValueFrom(
            CpmCServiceRequestApiService.getServiceRequestList({project_uuid: this.project.uuid}, {limit: 1})
        ).then((result) => (this.serviceRequestsCount = result.total));

        // TODO: Get files count when available
        // firstValueFrom(
        //     CpmCFileApiService.getUserFileList({
        //         parent_path: `/projects/${this.project.uuid}/`
        //     })
        // ).then((result) => (this.filesCount = result.total));

        // Object Store count
        firstValueFrom(
            CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxyList(
                'project',
                this.project.uuid,
                {},
                {limit: 1}
            )
        ).then((result) => (this.objectStoreCount = result.total));
    }

    // ===============================================================
    // ===============================================================

    /**
     * Opens the project modal in edit mode
     */
    protected openProjectEditModal(): void {
        if (!this.project) return;

        const ref = this.globalsService.dialogService.open(
            NewEditProjectModalComponent,
            NewEditProjectModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.EDIT,
                extended: {
                    existing: this.project
                }
            })
        );

        // Refresh project data when modal closes with changes
        ref.onClose.subscribe((result: IBaseCRUModalResultBase<NewEditProjectModalResult>) => {
            const updatedProject = result?.extended?.item;

            if (updatedProject && this.project) {
                // Update local project data
                Object.assign(this.project, {
                    name: updatedProject.name,
                    description: updatedProject.description
                });
                this.cdr.detectChanges();
            }
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Navigates to the specified tab in the parent page.
     * Updates URL query parameters to switch tabs.
     *
     * @param tab - The tab identifier to navigate to
     */
    protected navigateToTab(tab: string): void {
        this.globalsService.router.navigate([], {
            relativeTo: this.globalsService.activatedRoute,
            queryParams: {tab},
            queryParamsHandling: 'merge'
        });
    }
}
